import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import EmailIcon from '@mui/icons-material/Email'
import ImageIcon from '@mui/icons-material/Image'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import WarningIcon from '@mui/icons-material/Warning'
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Typography,
  useTheme,
} from '@mui/material'

import { FC, useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { MagicReport as MagicReportModel } from '../models/MagicReport.model'
import { ApiQueryService } from '../services'
import { NotFound } from './NotFound'

const apiQueryService = new ApiQueryService()

export const MagicReport: FC = () => {
  const [searchParams] = useSearchParams()
  const theme = useTheme()

  const [report, setReport] = useState<MagicReportModel | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteSuccess, setDeleteSuccess] = useState(false)

  // Get URL parameters
  const reportId = searchParams.get('reportId')
  const uuid = searchParams.get('uuid')
  const reportedAt = searchParams.get('reportedAt')
  const trailId = searchParams.get('trailId')
  const signature = searchParams.get('signature')

  const fetchReport = useCallback(async () => {
    setLoading(true)
    setNotFound(false)

    try {
      // Use the reportId in the path
      if (!reportId) {
        throw new Error('Report ID is required')
      }

      const data = await apiQueryService.get<MagicReportModel>({
        endpoint: `/obstruction-report/retrieve/${reportId}`,
        params: {
          reportedAt: reportedAt || '',
          signature: signature || '',
          trailId: trailId || '',
          uuid: uuid || '',
        },
      })

      if (data) {
        setReport(data)
        setError(null)
      } else {
        setNotFound(true)
      }
    } catch (err: any) {
      // Check if the error is a "not found" error
      if (
        err?.message?.includes('has expired') ||
        err?.message?.includes('not found') ||
        err?.response?.status === 404
      ) {
        setNotFound(true)
      } else {
        setError(err?.message || 'An error occurred while fetching the report')
      }
      console.error('Fetch error:', err)
    } finally {
      setLoading(false)
    }
  }, [reportId, uuid, reportedAt, trailId, signature])

  // Fetch report data
  useEffect(() => {
    fetchReport()
  }, [reportId, uuid, reportedAt, trailId, signature])

  // Handle delete
  const handleDelete = async () => {
    setLoading(true)
    try {
      // Use the reportId in the path
      if (!reportId) {
        throw new Error('Report ID is required')
      }

      const result = await apiQueryService.delete<boolean>({
        endpoint: `/obstruction-report/purge/${reportId}`,
        params: {
          reportedAt: reportedAt || '',
          signature: signature || '',
          trailId: trailId || '',
          uuid: uuid || '',
        },
      })

      // Check if the delete was successful
      if (result) {
        setDeleteSuccess(true)
        setDeleteError(null)
      } else {
        throw new Error('Failed to resolve report')
      }
    } catch (err: any) {
      setDeleteError(err?.message || 'An error occurred while deleting the report')
      console.error('Delete error:', err)
    } finally {
      setLoading(false)
    }
  }

  const getAlertLevelColor = (level: string) => {
    // Use direct color values instead of theme references for better visibility
    switch (level?.toUpperCase()) {
      case 'HIGH':
        return '#f44336' // red
      case 'MEDIUM':
        return '#ff9800' // orange
      case 'LOW':
        return '#4caf50' // green
      default:
        return '#2196f3' // blue
    }
  }

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleString()
  }

  const hasValidCoordinates = (lat: any, long: any) => {
    const num1 = Number(lat)
    const num2 = Number(long)
    return lat != null && !isNaN(num1) && num1 !== 0 && long != null && !isNaN(num2) && num2 !== 0
  }

  const getGoogleMapsLink = (lat: unknown, long: unknown) =>
    `https://www.google.com/maps/search/?api=1&query=${Number(lat).toFixed(6)},${Number(long).toFixed(6)}`

  // mlat/mlon drop the marker; #map centers on it. layers=Y is the CyclOSM style, which shows trails clearly.
  const getOpenStreetMapLink = (lat: unknown, long: unknown) => {
    const latStr = Number(lat).toFixed(6)
    const longStr = Number(long).toFixed(6)
    return `https://www.openstreetmap.org/?mlat=${latStr}&mlon=${longStr}&layers=Y#map=17/${latStr}/${longStr}`
  }

  // Show loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    )
  }

  // Show not found state
  if (notFound) {
    return <NotFound message="Obstruction Report Not Found" />
  }

  // Show error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    )
  }

  // If no report data
  if (!report) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No report data available.
      </Alert>
    )
  }

  // If report was successfully deleted, show success message
  if (deleteSuccess) {
    return (
      <Card sx={{ margin: '0 auto', maxWidth: 800, mt: 4 }}>
        <CardContent sx={{ py: 6, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 60, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Report Successfully Resolved
          </Typography>
          <Typography variant="body1" color="text.secondary">
            The obstruction report has been resolved.
          </Typography>
        </CardContent>
      </Card>
    )
  }

  // Check if obstruction type is "other" (case-insensitive)
  const isOtherObstructionType = report.obstructionType?.name.toLowerCase() === 'other'

  return (
    <Card sx={{ margin: '0 auto', maxWidth: 800, mt: 4 }}>
      <CardHeader
        title={`Obstruction Report`}
        subheader={`Trail: ${report.trail?.name || 'Unknown Trail'}`}
        action={
          report.alertLevel && (
            <Box sx={{ alignItems: 'center', display: 'flex', height: '100%' }}>
              <Chip
                icon={<WarningIcon style={{ fontSize: '20px', marginRight: 0 }} />}
                label={report.alertLevel}
                sx={{
                  '& .MuiChip-icon': {
                    alignItems: 'center',
                    color: '#ffffff',
                    display: 'flex',
                    marginLeft: '8px',
                    marginRight: '4px',
                  },
                  '& .MuiChip-label': {
                    alignItems: 'center',
                    display: 'flex',
                    fontSize: 16,
                    padding: '8px 12px 8px 6px',
                    textTransform: 'capitalize',
                  },
                  backgroundColor: getAlertLevelColor(report.alertLevel),
                  color: '#ffffff', // White text for better contrast
                  fontWeight: 'bold',
                  height: 'auto',
                }}
              />
            </Box>
          )
        }
        sx={{
          '& .MuiCardHeader-action': {
            alignSelf: 'center',
            margin: 0,
          },
        }}
      />
      <Divider />
      <CardContent>
        {deleteError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {deleteError}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {isOtherObstructionType
                ? report.statusDescription
                : `${report.obstructionType.name} ${report.obstructionType.symbol}` || 'Unspecified Obstruction'}
            </Typography>
            {report.obstructionDescription ? (
              <Typography variant="body1" color="text.secondary" paragraph>
                {report.obstructionDescription}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" paragraph>
                No description provided.
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" mb={1}>
              <CalendarTodayIcon sx={{ color: theme.palette.text.secondary, mr: 1 }} />
              <Typography variant="body2">Reported: {formatDate(report.reportedAt)}</Typography>
            </Box>
            <Box display="flex" alignItems="center" mb={1}>
              <EmailIcon sx={{ color: theme.palette.text.secondary, mr: 1 }} />
              <Typography variant="body2" color={report.reporterEmail ? 'inherit' : 'text.secondary'}>
                {report.reporterEmail || 'No email provided'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" mb={1}>
              <LocationOnIcon sx={{ color: theme.palette.text.secondary, mr: 1 }} />
              <Box>
                <Typography variant="subtitle1" color={(theme) => theme.palette.grey[500]}>
                  Location:{' '}
                  {hasValidCoordinates(report.gpsLat, report.gpsLong) ? (
                    // keep the pair together so a narrow screen wraps after "Location:", not between lat and long
                    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                      {`${Number(report.gpsLat).toFixed(6)}, ${Number(report.gpsLong).toFixed(6)}`}
                    </Box>
                  ) : (
                    'No location data available'
                  )}
                </Typography>
                {hasValidCoordinates(report.gpsLat, report.gpsLong) && (
                  <Typography variant="subtitle1" color={(theme) => theme.palette.grey[500]}>
                    <Link href={getGoogleMapsLink(report.gpsLat, report.gpsLong)} target="_blank" rel="noreferrer">
                      Google Maps
                    </Link>
                    {' · '}
                    <Link href={getOpenStreetMapLink(report.gpsLat, report.gpsLong)} target="_blank" rel="noreferrer">
                      OpenStreetMap
                    </Link>
                  </Typography>
                )}
              </Box>
            </Box>

            <Box display="flex" alignItems="center">
              <ImageIcon sx={{ color: theme.palette.text.secondary, mr: 1 }} />
              <Typography
                variant="body2"
                component={report.imageUrl ? 'a' : 'span'}
                href={report.imageUrl}
                target="_blank"
                sx={{
                  color: report.imageUrl ? theme.palette.primary.main : 'text.secondary',
                  textDecoration: report.imageUrl ? 'underline' : 'none',
                }}
              >
                {report.imageUrl ? 'View Image' : 'No image available'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
      <Divider />
      <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
        <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleDelete} disabled={loading}>
          {loading ? 'Resolving...' : 'Resolve Report'}
        </Button>
      </CardActions>
    </Card>
  )
}

MagicReport.displayName = 'MagicReport'
